<script setup lang="ts">
import { computed, ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { STATUS_CLOSED } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useHotelStore } from '@/store/HotelStore';
import { useTabComposable } from '../useTabComposable';
import { useDialog } from 'primevue/usedialog';
import AutoComplete from 'primevue/autocomplete';
import Dialog from 'primevue/dialog';
import Fluid from 'primevue/fluid';
import Select from 'primevue/select';
import ModalSelectDate from '@/components/modal/ModalSelectDate.vue';
import RoomCategoryFetchingModal from '../RoomCategoryFetchingModal.vue';

type DateRange = Date | string | undefined;
interface Props {
  instanceForm?: any;
  advancedForm: any;
  hotels?: any;
  validate: any;
  minDateRangeValue: DateRange;
  maxDateRangeValue: any;
  hotelOptionConfig: number;
  roomOptionConfig: number;
  hotelOption?: any;
  indexOption: number;
  isShowError?: boolean;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  addRoom: [indexOption: number, indexHotel: number];
  removeRoom: [indexOption: number, indexHotel: number];
  addHotel: [indexOption: number];
  removeHotel: [indexOption: number];
  'update:hotels': [value: any];
}>();

const hotelOptionData = computed({
  get() {
    return props.hotelOption;
  },
  set(value) {
    emits('update:hotels', value);
  }
});

const { t } = useI18n({ useScope: 'global' });
const { roomCategoryFetchingModalControl, hotelRoomPayload, filterHotel, getRoomCategoriesData, filterRoomOptions } = useTabComposable();

const currentLanguage = getCurrentLocale();
const hotelStore = useHotelStore();
const isAdvancedquoteForm = ref(true);
const dialog = useDialog();

const hotelMultipleOnlyHotel = computed(() => hotelStore.selectHotelMultipleOnlyHotel);
const hotelInstaStatus = ref([STATUS_CLOSED]);
const filterListHotel = ref();
const roomCategoryLoading = computed(() => hotelStore.selectLoading);
const hotelRoomCategoryOption = computed(() => hotelStore.selectHotelRoomCategory);

const handleOpenModalDate = (listDate: string[], callBackFn: any) => {
  const dialogRef = dialog.open(ModalSelectDate, {
    props: {
      header: t('advance_quote.alternative_date'),
      modal: true,
      style: {
        width: '560px'
      }
    },
    emits: {
      isSave: (dateSelected: string) => {
        dialogRef.close();
        callBackFn(dateSelected);
      }
    },
    data: {
      availableDates: listDate
    }
  });
};

const addRoom = (indexOption: number, indexHotel: number) => {
  emits('addRoom', indexOption, indexHotel);
};

const removeRoom = (indexOption: number, indexHotel: number) => {
  emits('removeRoom', indexOption, indexHotel);
};

const addHotel = (indexOption: number) => {
  emits('addHotel', indexOption);
};

const removeHotel = (indexOption: number) => {
  emits('removeHotel', indexOption);
};

const searchHotel = async (hotelEvent: any, index: number) => {
  let hotelList = [...hotelMultipleOnlyHotel.value[index]];
  if (isAdvancedquoteForm.value && props.advancedForm[0].groupTypeId) {
    hotelList = hotelList.filter((hotel) => hotel.groupTypes.includes(props.advancedForm[0].groupTypeId));
  }
  if (!isAdvancedquoteForm.value && props.instanceForm[0].groupTypeId) {
    hotelList = hotelList.filter((hotel) => hotel.groupTypes.includes(props.instanceForm[0].groupTypeId));
  }
  const hotelCodes = hotelOptionData.value.hotels.filter((item: any) => item.hotel && item.hotel.hCode).map((hotel: any) => hotel.hotel.hCode);
  hotelList = hotelList.filter((item: any) => !hotelCodes.includes(item.hCode));
  filterListHotel.value = filterHotel(hotelEvent, hotelList);
};

const eventHotelChanged = async (e: any, index: number) => {
  if (e.value && e.value.hCode) {
    hotelInstaStatus.value[index] = e.value.insta || STATUS_CLOSED;
  }
  const hotelRoomData = hotelRoomCategoryOption.value?.get(e.value?.hCode);

  if ((typeof e.value === 'object' && e.value?.hCode && !hotelRoomData?.length) || hotelRoomData?.[0]?.isPackagePlus) {
    hotelInstaStatus.value[index] = e.value.insta || STATUS_CLOSED;
    const payload = {
      dateDep: formatDate(props.hotels[index].dateRange[0], 'yyyyLLdd'),
      dateRet: formatDate(props.hotels[index].dateRange[1], 'yyyyLLdd'),
      duration: props.hotels[index].duration,
      destDep: props.hotels[index].destination.airportCode,
      productCode: e.value?.hCode,
      language: currentLanguage.toUpperCase(),
      nbAdult: props.hotels[index].adults,
      option: 'HOTEL',
      ...hotelRoomPayload
    };
    const limitData = {
      min: props.minDateRangeValue,
      max: props.maxDateRangeValue
    };
    getRoomCategoriesData(payload, e.value, e.value?.excludeHotelRoom, handleOpenModalDate, limitData);
  }
};

const clearHotel = (hotel: any, index: number) => {
  hotelInstaStatus.value.splice(index, 1);
  hotel.hotel = '';
  hotel.rooms = hotel.rooms.map((item) => {
    item.room = '';
    return item;
  });
};
</script>
<template>
  <div class="hotel-room-selection">
    <div v-for="(hotel, indexHotel) in hotelOptionData?.hotels" :key="indexHotel">
      <Fluid>
        <div class="field">
          <label for="hotel" class="long-text" v-tooltip.top="$t('advance_quote.form.hotel')">{{ $t('advance_quote.form.hotel') }}</label>
          <div class="flex align-items-center option-hotel-name-class">
            <AutoComplete
              v-model="hotel.hotel"
              :completeOnFocus="true"
              :suggestions="filterListHotel"
              id="hotel-selection"
              :disabled="!hotelOptionData.destination || !hotelOptionData.dateRange.length || !hotelOptionData.duration || (isAdvancedquoteForm ? !props.advancedForm[0].groupTypeId : !props.instanceForm[0].groupTypeId)"
              :optionLabel="getTextVariant(currentLanguage, 'name', 'nameFr')"
              :placeholder="$t('advance_quote.form.hotel')"
              @complete="searchHotel($event, indexOption)"
              @change="eventHotelChanged($event, indexOption)"
              forceSelection
              :class="isShowError && validate.hotels[0]?.$response.$errors[indexHotel].hotel[0]?.$message ? 'p-invalid w-full' : 'w-full'"
            >
              <template #option="slotProps">
                <div class="flex align-options-center">
                  <div>
                    <div>{{ getTextVariant(currentLanguage, `${slotProps.option.name} (${slotProps.option.destName})`, `${slotProps.option.nameFr} (${slotProps.option.destNameFr})`) }}</div>
                  </div>
                </div>
              </template>
            </AutoComplete>
            <div class="clear-hotel-class">
              <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearHotel(hotel, indexOption)"></i>
            </div>
          </div>
          <span class="text-red-500" v-if="isShowError">
            {{ validate.hotels[0]?.$response.$errors[indexHotel].hotel[0]?.$message }}
          </span>
          <div class="room-class" v-for="(room, indexRoom) in hotel.rooms" :key="indexRoom">
            <Select
              id="room-id"
              v-model="room.room"
              :options="filterRoomOptions(hotelRoomCategoryOption?.get(hotel?.hotel?.hCode), hotel.rooms, true)"
              :optionLabel="getTextVariant(currentLanguage, 'roomDescription', 'roomDescriptionFr')"
              :placeholder="$t('advance_quote.form.select_room')"
              :emptyMessage="$t('common.no_available_option')"
              :disabled="!hotel.hotel"
              :loading="roomCategoryLoading"
              :class="isShowError && validate.hotels[0]?.$response.$errors[indexHotel].rooms?.[0]?.$response.$errors?.[indexRoom].room[0]?.$message ? 'p-invalid w-full mt-2' : 'w-full mt-2'"
            >
              <template #value="{ value, placeholder }">
                <div v-if="value">
                  {{ getTextVariant(currentLanguage, value.roomDescription, value.roomDescriptionFr) }}
                  <span v-if="value.meal">-{{ value.meal }}</span>
                </div>
                <div v-else>{{ placeholder }}</div>
              </template>
              <template #option="slotProps">
                <div>
                  {{ getTextVariant(currentLanguage, slotProps.option.roomDescription, slotProps.option.roomDescriptionFr) }}
                  <span v-if="slotProps.option.meal">- {{ slotProps.option.meal }}</span>
                </div>
              </template>
            </Select>
            <span class="text-red-500" v-if="isShowError">
              {{ validate.hotels[0]?.$response.$errors[indexHotel].rooms?.[0]?.$response?.$errors?.[indexRoom].room[0]?.$message }}
            </span>
          </div>
        </div>
      </Fluid>
      <div class="grid">
        <div class="flex w-full justify-content-around my-3">
          <div id="remove-room-btn" class="block text-primary cursor-pointer remove-last-room-class" @click="removeRoom(indexOption, indexHotel)" v-if="hotel?.rooms?.length > 1">- {{ $t('advance_quote.form.remove_room') }}</div>
          <div id="add-room-btn" v-if="hotel?.rooms?.length < roomOptionConfig" class="block text-primary add-room-button" @click="addRoom(indexOption, indexHotel)">+ {{ $t('advance_quote.form.add_room') }}</div>
        </div>
      </div>
    </div>
    <div class="grid">
      <div class="flex w-full justify-content-around mb-3">
        <div id="remove-hotel-btn" class="block text-primary cursor-pointer" @click="removeHotel(indexOption)" v-if="hotelOptionData?.hotels?.length > 1">- {{ $t('advance_quote.form.remove_hotel') }}</div>
        <div id="add-hotel-btn" class="block text-primary cursor-pointer" v-if="hotelOptionData?.hotels?.length < hotelOptionConfig" @click="addHotel(indexOption)">+ {{ $t('advance_quote.form.add_hotel') }}</div>
      </div>
    </div>
    <Dialog v-model:visible="roomCategoryFetchingModalControl.visible" modal :header="roomCategoryFetchingModalControl.isFetching ? $t('advance_quote.searching_for_room') : $t('advance_quote.no_rooms_available')">
      <RoomCategoryFetchingModal :is-fetching="roomCategoryFetchingModalControl.isFetching" @closeModal="roomCategoryFetchingModalControl.visible = false"></RoomCategoryFetchingModal>
    </Dialog>
  </div>
</template>
